'use client';

import type { LottieRefCurrentProps } from 'lottie-react';
import { useEffect, useRef } from 'react';

/** Delay before a hover plays the animation, so brushing past a nav item
 *  (hover intent) doesn't trigger it. */
export const HOVER_PLAY_DELAY_MS = 200;

interface UseLottieHoverActivePlayOptions {
  /** The item is hovered — plays once after HOVER_PLAY_DELAY_MS if still hovered. */
  hovered: boolean;
  /** The item is active/selected — plays once immediately (no delay). */
  active: boolean;
}

/**
 * Shared playback control for nav Lottie icons: plays once from the start when
 * the item becomes active (immediately) and when it stays hovered past a short
 * intent delay. Returns the ref to attach to the `<Lottie>` element.
 */
export function useLottieHoverActivePlay({ hovered, active }: UseLottieHoverActivePlayOptions) {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (!hovered) return;
    const id = setTimeout(() => lottieRef.current?.goToAndPlay(0), HOVER_PLAY_DELAY_MS);
    return () => clearTimeout(id);
  }, [hovered]);

  useEffect(() => {
    if (active) lottieRef.current?.goToAndPlay(0);
  }, [active]);

  return lottieRef;
}
